import type { DiscoveryApi, PricingAddon } from '../data/discovery';
import { endpointActionOf } from '../features/catalog/editorial';
import type { ApiDetail, ProviderQuote } from '../features/catalog/model';
import { platformInfoOf } from '../lib/catalogMap';
import type { DisplayPrice } from '../lib/pricing';
import { baseLocale, getLocale } from '../paraglide/runtime';
import { catalogText } from './catalogText';

/**
 * The read seams for catalog data text, one per shape: a platform detail as its
 * pages read it, a discovery API as the playground reads it, and an endpoint's
 * input schema. English passes through
 * untouched, the same object back, so an English page renders exactly what it
 * rendered before translations existed.
 */

const localizedAddons = (addons: PricingAddon[]): PricingAddon[] =>
  addons.map((addon) => ({ ...addon, label: catalogText(addon.label) }));

const localizedPrice = (price: DisplayPrice): DisplayPrice =>
  price.addons ? { ...price, addons: localizedAddons(price.addons) } : price;

const localizedQuote = (quote: ProviderQuote): ProviderQuote => ({
  ...quote,
  price: localizedPrice(quote.price),
});

/** The blurb, each endpoint's action and description, and its sources' addon labels. */
export const localizedDetail = (detail: ApiDetail | null): ApiDetail | null => {
  if (!detail || getLocale() === baseLocale) return detail;
  return {
    ...detail,
    blurb: catalogText(detail.blurb),
    endpoints: detail.endpoints.map((endpoint) => ({
      ...endpoint,
      action: catalogText(endpoint.action),
      description: catalogText(endpoint.description),
      providers: endpoint.providers.map(localizedQuote),
      ...(endpoint.notServing ? { notServing: endpoint.notServing.map(localizedQuote) } : {}),
    })),
  };
};

const localizedApi = (api: DiscoveryApi): DiscoveryApi => {
  const from = api.pricing.from;
  return {
    ...api,
    description: catalogText(api.description),
    ...(from.model === 'linear' && from.addons
      ? { pricing: { ...api.pricing, from: { ...from, addons: localizedAddons(from.addons) } } }
      : {}),
  };
};

/** The heading the playground shows for an API. The gateway's `name` is English
 *  only, so a translated locale composes it the way the endpoint pages title an
 *  endpoint: the platform name, then the translated action title. */
export const localizedApiTitle = (api: DiscoveryApi): string =>
  getLocale() === baseLocale
    ? api.name
    : `${platformInfoOf(api.slug).name} ${catalogText(endpointActionOf(api.slug))}`;

/** Each API's description and its published offer's addon labels, as the playground shows them. */
export const localizedApis = (apis: DiscoveryApi[]): DiscoveryApi[] =>
  getLocale() === baseLocale ? apis : apis.map(localizedApi);

const asObject = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

/**
 * The input schema's top-level field descriptions, the ones the try form and
 * the spec table show. The response schema stays English: it is the API
 * reference, read next to the JSON it describes.
 */
export const localizedInputSchema = <T extends { inputSchema?: unknown }>(schemas: T): T => {
  const root = asObject(schemas.inputSchema);
  const properties = asObject(root?.properties);
  if (!root || !properties || getLocale() === baseLocale) return schemas;
  const translated = Object.entries(properties).map(([name, value]) => {
    const field = asObject(value);
    return typeof field?.description === 'string'
      ? [name, { ...field, description: catalogText(field.description) }]
      : [name, value];
  });
  return { ...schemas, inputSchema: { ...root, properties: Object.fromEntries(translated) } };
};
